import { useEffect, useMemo, useState, useCallback } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, MapPin, CalendarClock, Briefcase, Users as UsersIcon, MessageCircle, Pencil, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; }
function endOfMonth(d) { const x = new Date(d.getFullYear(), d.getMonth() + 1, 0); x.setHours(23,59,59,999); return x; }
function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d) {
  const x = new Date(d); x.setHours(0,0,0,0);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  return x;
}
// Local-time date key (YYYY-MM-DD). Critical: we do NOT use toISOString()
// here because in IST (UTC+5:30) it shifts the local-midnight Date back
// to the previous calendar day in UTC, which made every event in the
// Month grid render in the wrong cell (off-by-one). Both the grid cells
// and the event-by-day map MUST use the same local-time key.
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function sameDay(a, b) { return a.toDateString() === b.toDateString(); }

// Every LOCAL day an event spans (start_at → end_at, capped at 31 days) so
// multi-day trainings/visits show on EVERY day in Month AND Week views.
function eventDayKeys(e) {
  if (!e.start_at) return [];
  const start = new Date(e.start_at);
  const keys = [isoDate(start)];
  if (e.end_at) {
    const end = new Date(e.end_at);
    let d = new Date(start); d.setHours(0, 0, 0, 0);
    for (let i = 0; i < 31; i++) {
      d = addDays(d, 1);
      if (isoDate(d) > isoDate(end)) break;
      keys.push(isoDate(d));
    }
  }
  return keys;
}

function NewMeetingDialog({ open, onOpenChange, onCreated, defaultStart, defaultType = "meeting", editingMeeting = null }) {
  const isEdit = !!editingMeeting;
  const [form, setForm] = useState({ title: "", client_id: "", deal_id: "", start_at: "", end_at: "", location: "", notes: "", attendee_user_ids: [], meeting_type: "meeting", demo_type: "", products_demonstrated: [], purpose: "" });
  const [clients, setClients] = useState([]);
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Editing path — hydrate from the meeting object directly so the dialog
    // doubles as both "create" and "edit" without duplicating the entire UI.
    if (isEdit) {
      const tz = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      };
      const hydrate = (m) => setForm({
        title: m.title || "",
        client_id: m.client_id || "",
        deal_id: m.deal_id || "",
        start_at: tz(m.start_at),
        end_at: tz(m.end_at),
        location: m.location || "",
        notes: m.notes || "",
        attendee_user_ids: m.attendee_user_ids || [],
        meeting_type: m.meeting_type || "meeting",
        demo_type: m.demo_type || "",
        products_demonstrated: m.products_demonstrated || [],
        purpose: m.purpose || "",
      });
      // Calendar feed events only carry a subset of fields — hydrate from
      // the full meeting doc so the edit form is always pre-filled.
      hydrate(editingMeeting);
      const realId = editingMeeting.meeting_id || editingMeeting.ref_id || editingMeeting.id;
      if (realId) {
        api.get(`/meetings/${realId}`).then((r) => hydrate(r.data)).catch(() => {});
      }
    } else {
      const dt = defaultStart ? new Date(defaultStart) : new Date();
      dt.setMinutes(0, 0, 0);
      const start = new Date(dt); start.setHours(start.getHours() + 1);
      const end = new Date(start); end.setHours(end.getHours() + 1);
      const tz = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      const defaultPurpose = defaultType === "demo" ? "demonstration" : defaultType === "site_visit" ? "onsite_service" : "";
      setForm({ title: "", client_id: "", deal_id: "", start_at: tz(start), end_at: tz(end), location: "", notes: "", attendee_user_ids: [], meeting_type: defaultType, demo_type: "", products_demonstrated: [], purpose: defaultPurpose });
    }
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/deals").then((r) => setDeals(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [open, defaultStart, defaultType, isEdit, editingMeeting]);

  const submit = async () => {
    if (!form.title || !form.start_at) { toast.error("Title and start required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        client_id: form.client_id || null,
        deal_id: form.deal_id || null,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        location: form.location || null,
        notes: form.notes || null,
        attendee_user_ids: form.attendee_user_ids,
        meeting_type: form.meeting_type,
        demo_type: form.meeting_type === "demo" ? (form.demo_type || null) : null,
        products_demonstrated: form.meeting_type === "demo" ? form.products_demonstrated : [],
        purpose: form.purpose || null,
      };
      if (isEdit) {
        await api.patch(`/meetings/${editingMeeting.meeting_id || editingMeeting.ref_id}`, payload);
        toast.success("Meeting updated");
      } else {
        await api.post("/meetings", payload);
        const labels = { meeting: "Meeting", demo: "Demo", site_visit: "Site visit" };
        toast.success(`${labels[form.meeting_type] || "Meeting"} scheduled`);
      }
      onCreated?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  const isDemo = form.meeting_type === "demo";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{isEdit ? "Edit" : "New"} {isDemo ? "demo" : form.meeting_type === "site_visit" ? "site visit" : "meeting"}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {isDemo
              ? "Track product demos given to potential customers — earns +12 on schedule and +25 on completion."
              : "Schedule a meeting and notify attendees automatically."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Type segmented control — drives downstream points + reports. */}
          <div>
            <Label className="text-xs font-semibold">Type</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1.5" data-testid="meeting-type-toggle">
              {[
                { k: "meeting", label: "Meeting" },
                { k: "demo", label: "Demo" },
                { k: "site_visit", label: "Site visit" },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setForm({ ...form, meeting_type: t.k })}
                  className={`h-10 rounded-2xl text-sm font-semibold tap transition border ${form.meeting_type === t.k ? "bg-primary text-primary-foreground border-primary shadow-soft" : "bg-muted/40 text-muted-foreground border-border/60"}`}
                  data-testid={`meeting-type-${t.k}`}
                >{t.label}</button>
              ))}
            </div>
          </div>

          <div><Label className="text-xs font-semibold">Title *</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={isDemo ? "e.g. Vibration kit demo at Acme" : "e.g. Catch-up with Acme"} data-testid="meeting-title-input" /></div>
          <div>
            <Label className="text-xs font-semibold">Purpose of visit / activity</Label>
            <Select value={form.purpose || "__none"} onValueChange={(v) => setForm({ ...form, purpose: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="meeting-purpose-select"><SelectValue placeholder="Select purpose…" /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__none">— Unspecified</SelectItem>
                <SelectItem value="lead_prospecting">Lead / Prospecting</SelectItem>
                <SelectItem value="lead_qualification">Lead Qualification</SelectItem>
                <SelectItem value="demonstration">Demonstration</SelectItem>
                <SelectItem value="follow_up">Follow-ups</SelectItem>
                <SelectItem value="negotiation">Negotiations</SelectItem>
                <SelectItem value="onsite_training">On-site Training</SelectItem>
                <SelectItem value="technical_support">Technical Support</SelectItem>
                <SelectItem value="onsite_service">Technical / On-site Services</SelectItem>
                <SelectItem value="events_exhibition">Events / Exhibition / Conferences</SelectItem>
                <SelectItem value="tech_orientation">Technology Orientation Program</SelectItem>
                <SelectItem value="internal_meeting">Internal Meetings</SelectItem>
                <SelectItem value="wfh_online_meeting">WFH Online Meetings</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Start *</Label>
              <Input type="datetime-local" className="rounded-2xl h-11 mt-1.5" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="meeting-start-input" /></div>
            <div><Label className="text-xs font-semibold">End</Label>
              <Input type="datetime-local" className="rounded-2xl h-11 mt-1.5" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs font-semibold">Location</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="On-site / Zoom link / Office" /></div>

          {/* Demo-specific fields */}
          {isDemo && (
            <div className="space-y-3 p-3 rounded-2xl bg-primary-50/30 border border-primary-200/60" data-testid="demo-fields">
              <div>
                <Label className="text-xs font-semibold">Demo type</Label>
                <Select value={form.demo_type || "__none"} onValueChange={(v) => setForm({ ...form, demo_type: v === "__none" ? "" : v })}>
                  <SelectTrigger className="rounded-2xl h-11 mt-1.5 bg-white" data-testid="demo-type-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    <SelectItem value="__none">— Unspecified</SelectItem>
                    <SelectItem value="product">Product demo</SelectItem>
                    <SelectItem value="virtual">Virtual / online</SelectItem>
                    <SelectItem value="on_site">On-site at customer</SelectItem>
                    <SelectItem value="walkthrough">Walkthrough at office</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {products.length > 0 && (
                <div>
                  <Label className="text-xs font-semibold">Products demonstrated</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="demo-products-picker">
                    {products.slice(0, 12).map((p) => {
                      const sel = form.products_demonstrated.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setForm({ ...form, products_demonstrated: sel ? form.products_demonstrated.filter((x) => x !== p.id) : [...form.products_demonstrated, p.id] })}
                          className={`px-2.5 py-1 text-[11px] rounded-full border tap ${sel ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border"}`}
                        >{p.name}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Client</Label>
              <Select value={form.client_id || "__none"} onValueChange={(v) => setForm({ ...form, client_id: v === "__none" ? "" : v })}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="rounded-2xl"><SelectItem value="__none">—</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold">Deal</Label>
              <Select value={form.deal_id || "__none"} onValueChange={(v) => setForm({ ...form, deal_id: v === "__none" ? "" : v })}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="rounded-2xl"><SelectItem value="__none">—</SelectItem>{deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {users.length > 0 && (
            <div>
              <Label className="text-xs font-semibold">Internal attendees</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {users.map((u) => {
                  const sel = form.attendee_user_ids.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setForm({ ...form, attendee_user_ids: sel ? form.attendee_user_ids.filter((x) => x !== u.id) : [...form.attendee_user_ids, u.id] })}
                      className={`px-2.5 py-1 text-[11px] rounded-full border tap ${sel ? "bg-primary text-primary-foreground border-primary" : "bg-muted border-border"}`}
                    >
                      {u.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div><Label className="text-xs font-semibold">Notes</Label><Textarea className="rounded-2xl mt-1.5" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl tap" data-testid="meeting-save-button">{saving ? "Saving…" : isEdit ? "Save changes" : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Build a 2-3 word label for an event so the month view stays readable
// at a glance. Output shape: "{Kind} {Place}" — e.g. "Visit Mumbai",
// "Demo Pune", "Training Hyderabad", "Follow-up Acme".
//
// - Kind comes from the event type / meeting_type, with a fallback that
//   scans the title text for keywords (training, visit, demo).
// - Place is the first useful word from `location`, falling back to the
//   first word of `client_name` or `deal_title`. URL / Zoom / phone-like
//   locations are skipped so we don't pollute the chip with "https://…".
function shortEventLabel(e) {
  let kind;
  if (e.type === "followup") kind = "Follow-up";
  else if (e.type === "external") kind = "Ext";
  else if (e.meeting_type === "demo") kind = "Demo";
  else if (e.meeting_type === "site_visit") kind = "Visit";
  else if (e.meeting_type === "training") kind = "Training";
  else if (/training/i.test(e.title || "")) kind = "Training";
  else if (/\b(visit|on[- ]?site)\b/i.test(e.title || "")) kind = "Visit";
  else if (/\bdemo\b/i.test(e.title || "")) kind = "Demo";
  else kind = "Meeting";

  let place = "";
  const loc = (e.location || "").trim();
  if (loc && !/^https?:|^zoom|^https?:\/\/|@/i.test(loc) && !/^\+?\d[\d\s-]{4,}$/.test(loc)) {
    place = loc.split(/[\s,·\-/|]+/)[0];
  } else if (e.client_name) {
    place = String(e.client_name).split(/\s+/)[0];
  } else if (e.deal_title) {
    place = String(e.deal_title).split(/\s+/)[0];
  }
  // Cap place to 14 chars so long company names ("Mahindra-Logistics-Pvt")
  // don't break the layout.
  if (place.length > 14) place = place.slice(0, 14);
  return place ? `${kind} · ${place}` : kind;
}

function MonthGrid({ cursor, eventsByDay, today, onDayClick }) {
  const monthStart = startOfMonth(cursor);
  const gridStart = startOfWeek(monthStart);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  return (
    <div className="rounded-3xl border border-border/60 bg-card shadow-soft overflow-hidden">
      <div className="grid grid-cols-7 bg-muted/40 border-b border-border/60">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-1 sm:px-3 py-2 text-center sm:text-left text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.18em] font-bold text-muted-foreground">
            <span className="sm:hidden">{w[0]}</span>
            <span className="hidden sm:inline">{w}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6 min-h-[420px] sm:min-h-[560px]">
        {days.map((d) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const evts = eventsByDay[isoDate(d)] || [];
          return (
            <button
              key={d.toISOString()}
              onClick={() => onDayClick(d)}
              data-testid={`day-${isoDate(d)}`}
              className={`text-left p-1.5 sm:p-2 border-r border-b border-border/40 last:border-r-0 transition tap min-h-[68px] sm:min-h-0 ${
                inMonth ? "bg-card hover:bg-muted/30" : "bg-muted/30 text-muted-foreground"
              } ${isToday ? "ring-2 ring-primary ring-inset" : ""}`}
            >
              <div className={`flex items-center justify-between mb-1 ${isToday ? "text-primary" : ""}`}>
                <span className={`text-xs font-bold ${isToday ? "h-5 w-5 rounded-full bg-primary text-primary-foreground grid place-items-center" : ""}`}>{d.getDate()}</span>
              </div>
              {/* Compact 2-3 word chips per event so users can read the day at a
                  glance ("Visit Mumbai", "Demo Pune", "Follow-up Acme"). Up to
                  3 chips visible — overflow shown as "+N". Color-coded by type
                  (primary=meeting, emerald=pinged-followup, amber=followup,
                  sky=external). Hover/title reveals the full title. */}
              <div className="flex flex-col gap-0.5 sm:gap-1">
                {evts.slice(0, 3).map((e) => {
                  const isPinged = e.type === "followup" && e.client_pinged_at;
                  const cls = e.type === "meeting"
                    ? "bg-primary/15 text-primary-700 border-primary/30"
                    : isPinged
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : e.type === "external"
                    ? "bg-sky-50 text-sky-800 border-sky-200"
                    : "bg-amber-50 text-amber-800 border-amber-200";
                  return (
                    <span
                      key={e.id}
                      title={`${e.title}${e.owner_name ? ` · ${e.owner_name}` : ""}${e.client_name ? " · " + e.client_name : ""}${e.location ? " · " + e.location : ""}`}
                      className={`block text-[9px] sm:text-[10px] leading-tight font-semibold rounded-md border px-1 py-0.5 truncate ${cls}`}
                      data-testid={`day-event-${e.id}`}
                    >
                      {shortEventLabel(e)}
                    </span>
                  );
                })}
                {evts.length > 3 && (
                  <span className="text-[8px] sm:text-[9px] font-semibold text-muted-foreground pl-1">+{evts.length - 3} more</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Inline action row for a scheduled meeting on the calendar card. Lets
// the owner (or admin/manager) edit the meeting, mark it completed, or
// cancel it without leaving the calendar.
function MeetingActions({ meeting, onChanged, dayKey }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const realId = meeting.meeting_id || meeting.ref_id;
  // Day-wise completion for multi-day events: each spanned date completes
  // independently; the whole event flips to "completed" once every day is done.
  const allDays = eventDayKeys(meeting);
  const multiDay = allDays.length > 1;
  const dayDone = multiDay && dayKey && (meeting.completed_days || []).includes(dayKey);

  const onComplete = async () => {
    if (!realId) return;
    setBusy(true);
    try {
      await api.post(`/meetings/${realId}/complete`,
        multiDay && dayKey ? { day: dayKey, all_days: allDays } : {});
      toast.success(multiDay && dayKey ? `${dayKey} marked as completed` : "Marked as completed");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const onCancel = async () => {
    if (!realId) return;
    const reason = window.prompt("Reason (optional):", "");
    if (reason === null) return;  // user hit Cancel on prompt
    setBusy(true);
    try {
      await api.post(`/meetings/${realId}/cancel`, { reason: reason || null });
      toast.success("Meeting cancelled");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!realId) return;
    if (!window.confirm(`Delete this event permanently?\n\n"${meeting.title}"\n\nIt will be removed from every calendar view.`)) return;
    setBusy(true);
    try {
      await api.delete(`/meetings/${realId}`);
      toast.success("Event deleted");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const isScheduled = (meeting.status || "scheduled") === "scheduled";

  return (
    <>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        {dayDone && (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider" data-testid={`meeting-day-done-${realId}`}>
            <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> This day done
          </Badge>
        )}
        {isScheduled && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing(true)}
          disabled={busy}
          className="rounded-xl h-7 text-[11px] px-2 tap"
          data-testid={`meeting-edit-${realId}`}
        >
          <Pencil className="h-3 w-3 mr-1" /> Edit
        </Button>
        )}
        {isScheduled && !dayDone && (
        <Button
          variant="outline"
          size="sm"
          onClick={onComplete}
          disabled={busy}
          className="rounded-xl h-7 text-[11px] px-2 tap text-emerald-700 hover:bg-emerald-50 border-emerald-200"
          data-testid={`meeting-complete-${realId}`}
        >
          <CheckCircle2 className="h-3 w-3 mr-1" /> {multiDay && dayKey ? "Complete this day" : "Completed"}
        </Button>
        )}
        {isScheduled && (
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={busy}
          className="rounded-xl h-7 text-[11px] px-2 tap text-red-600 hover:bg-red-50 border-red-200"
          data-testid={`meeting-cancel-${realId}`}
        >
          <XCircle className="h-3 w-3 mr-1" /> Cancelled
        </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={busy}
          className="rounded-xl h-7 text-[11px] px-2 tap text-red-600 hover:bg-red-50 border-red-200"
          data-testid={`meeting-delete-${realId}`}
        >
          <Trash2 className="h-3 w-3 mr-1" /> Delete
        </Button>
      </div>
      <NewMeetingDialog
        open={editing}
        onOpenChange={setEditing}
        editingMeeting={meeting}
        onCreated={onChanged}
      />
    </>
  );
}

// Complete / Delete controls for follow-up events — pipeline-created
// follow-ups behave like regular calendar events now.
function FollowupActions({ event, onChanged }) {
  const fuId = event.ref_id;
  const [busy, setBusy] = useState(false);
  if (!fuId) return null;

  const onComplete = async () => {
    setBusy(true);
    try {
      await api.post(`/followups/${fuId}/complete`);
      toast.success("Follow-up completed");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete this follow-up task?\n\n"${event.title}"\n\nThis removes it permanently from the calendar and follow-up list.`)) return;
    setBusy(true);
    try {
      await api.delete(`/followups/${fuId}`);
      toast.success("Follow-up deleted");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        onClick={onComplete}
        disabled={busy}
        className="rounded-xl h-7 text-[11px] px-2 tap text-emerald-700 hover:bg-emerald-50 border-emerald-200"
        data-testid={`followup-complete-${fuId}`}
      >
        <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onDelete}
        disabled={busy}
        className="rounded-xl h-7 text-[11px] px-2 tap text-red-600 hover:bg-red-50 border-red-200"
        data-testid={`followup-delete-${fuId}`}
      >
        <Trash2 className="h-3 w-3 mr-1" /> Delete
      </Button>
    </div>
  );
}

function WeekList({ cursor, events, today, onChanged }) {  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="space-y-3">
      {days.map((d) => {
        const dayEvts = events.filter((e) => e.start_at && eventDayKeys(e).includes(isoDate(d))).sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
        const isToday = sameDay(d, today);
        return (
          <Card key={d.toISOString()} className={`rounded-3xl border-border/60 shadow-soft ${isToday ? "ring-2 ring-primary" : ""}`}>
            <CardContent className="p-5">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{WEEKDAYS[(d.getDay() + 6) % 7]}</p>
                  <p className={`font-display text-2xl font-black tracking-tight ${isToday ? "text-primary" : ""}`}>{MONTHS[d.getMonth()]} {d.getDate()}</p>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{dayEvts.length} event{dayEvts.length !== 1 ? "s" : ""}</Badge>
              </div>
              {dayEvts.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Nothing scheduled.</p>
              ) : (
                <div className="space-y-2">
                  {dayEvts.map((e) => {
                    const time = e.start_at && !e.all_day ? new Date(e.start_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }) : "All day";
                    const Icon = e.type === "meeting" ? Briefcase : e.type === "external" ? CalendarClock : CalendarClock;
                    const cls = e.type === "meeting"
                      ? "bg-primary-50 text-primary-700 border-primary-200"
                      : e.type === "external"
                      ? "bg-sky-50 text-sky-800 border-sky-200"
                      : "bg-amber-50 text-amber-800 border-amber-200";
                    return (
                      <div key={e.id} className="flex items-start gap-3 p-3 rounded-2xl border border-border/60" data-testid={`week-event-${e.id}`}>
                        <div className={`h-9 w-9 rounded-2xl grid place-items-center shrink-0 border ${cls}`}><Icon className="h-4 w-4" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{e.title}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {time}
                            {e.client_name ? ` · ${e.client_name}` : ""}
                            {e.client_contact_person ? ` · ${e.client_contact_person}` : ""}
                            {e.deal_title ? ` · ${e.deal_title}` : ""}
                            {e.feed_label ? ` · ${e.feed_label}` : ""}
                          </p>
                          {e.owner_name && (
                            <Badge
                              variant="outline"
                              className="mt-1.5 mr-1 bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] uppercase tracking-wider"
                              data-testid={`week-event-owner-${e.id}`}
                            >
                              {e.owner_name}
                            </Badge>
                          )}
                          {e.location && <p className="text-[11px] text-muted-foreground mt-0.5 inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{e.location}</p>}
                          {/* Surface meeting sub-type — `demo` is the high-impact
                              case, so render a primary-toned chip; site visits
                              get an amber chip; regular meetings stay quiet. */}
                          {e.type === "meeting" && e.meeting_type === "demo" && (
                            <Badge variant="outline" className="mt-1.5 bg-primary-50 text-primary-700 border-primary-200 text-[10px] uppercase tracking-wider">
                              Demo
                            </Badge>
                          )}
                          {e.type === "meeting" && e.meeting_type === "site_visit" && (
                            <Badge variant="outline" className="mt-1.5 bg-amber-50 text-amber-800 border-amber-200 text-[10px] uppercase tracking-wider">
                              Site visit
                            </Badge>
                          )}
                          {e.type === "meeting" && e.status === "completed" && (
                            <Badge variant="outline" className="mt-1.5 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider">
                              Completed
                            </Badge>
                          )}
                          {e.type === "meeting" && e.status === "cancelled" && (
                            <Badge variant="outline" className="mt-1.5 bg-red-50 text-red-700 border-red-200 text-[10px] uppercase tracking-wider">
                              Cancelled
                            </Badge>
                          )}
                          {e.client_pinged_at && (
                            <Badge variant="outline" className="mt-1.5 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                              <MessageCircle className="h-2.5 w-2.5" /> Pinged via {e.client_pinged_via || "WhatsApp"}
                            </Badge>
                          )}
                          {e.type === "meeting" && (
                            <MeetingActions meeting={e} onChanged={onChanged} dayKey={isoDate(d)} />
                          )}
                          {e.type === "followup" && (
                            <FollowupActions event={e} onChanged={onChanged} />
                          )}
                        </div>
                        {e.type === "followup" && e.client_phone && (
                          <div className="flex items-center gap-1.5">
                            <CallButton phone={e.client_phone} testId={`cal-call-${e.id}`} />
                            <WhatsAppButton
                              phone={e.client_phone}
                              email={e.client_email}
                              contactName={e.client_contact_person || e.client_name}
                              dealTitle={e.deal_title}
                              dealId={e.deal_id}
                              clientId={e.client_id}
                              clientCompany={e.client_name}
                              action={e.title}
                              defaultTemplate="follow_up_action"
                              followupId={e.ref_id}
                              label="Send"
                              onSent={onChanged}
                              testId={`cal-wa-${e.id}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// Month-view day panel — clicking a day shows every event on it with the
// SAME status badges & actions as the Week view (complete / cancel / delete),
// keeping both views fully synchronized. "+ New meeting" schedules on that day.
function DayPanel({ date, events, onClose, onChanged, onAdd }) {
  if (!date) return null;
  const dayKey = isoDate(date);
  return (
    <Dialog open={!!date} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="day-panel">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {WEEKDAYS[(date.getDay() + 6) % 7]}, {MONTHS[date.getMonth()]} {date.getDate()}
          </DialogTitle>
        </DialogHeader>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nothing scheduled.</p>
        ) : (
          <div className="space-y-2">
            {events.map((e) => {
              const time = e.start_at && !e.all_day ? new Date(e.start_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }) : "All day";
              return (
                <div key={e.id} className="p-3 rounded-2xl border border-border/60" data-testid={`day-panel-event-${e.id}`}>
                  <p className="font-semibold text-sm">{e.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {time}
                    {e.client_name ? ` · ${e.client_name}` : ""}
                    {e.owner_name ? ` · ${e.owner_name}` : ""}
                    {e.location ? ` · ${e.location}` : ""}
                  </p>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {e.type === "meeting" && e.meeting_type === "demo" && (
                      <Badge variant="outline" className="bg-primary-50 text-primary-700 border-primary-200 text-[10px] uppercase tracking-wider">Demo</Badge>
                    )}
                    {e.type === "meeting" && e.meeting_type === "site_visit" && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] uppercase tracking-wider">Site visit</Badge>
                    )}
                    {e.type === "meeting" && e.status === "completed" && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider" data-testid={`day-panel-completed-${e.id}`}>Completed</Badge>
                    )}
                    {e.type === "meeting" && e.status === "cancelled" && (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] uppercase tracking-wider">Cancelled</Badge>
                    )}
                  </div>
                  {/* Full saved details — everything the salesperson entered
                      (corrections doc: month-view details must not be blank). */}
                  {e.type === "meeting" && (
                    <div className="mt-2 text-xs space-y-0.5 text-muted-foreground" data-testid={`day-panel-details-${e.id}`}>
                      <p><strong className="text-foreground">Type:</strong> {(e.meeting_type || "meeting").replace("_", " ")}{e.demo_type ? ` (${e.demo_type})` : ""}</p>
                      {e.purpose && <p><strong className="text-foreground">Purpose:</strong> {String(e.purpose).replace(/_/g, " ")}</p>}
                      <p>
                        <strong className="text-foreground">When:</strong>{" "}
                        {e.start_at ? new Date(e.start_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}
                        {e.end_at ? ` → ${new Date(e.end_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}` : ""}
                      </p>
                      {e.client_name && <p><strong className="text-foreground">Client:</strong> {e.client_name}</p>}
                      {e.deal_title && <p><strong className="text-foreground">Deal:</strong> {e.deal_title}</p>}
                      {e.location && <p><strong className="text-foreground">Location:</strong> {e.location}</p>}
                      {(e.attendee_names || []).length > 0 && <p><strong className="text-foreground">Attendees:</strong> {e.attendee_names.join(", ")}</p>}
                      {e.notes && <p><strong className="text-foreground">Notes:</strong> {e.notes}</p>}
                    </div>
                  )}
                  {e.type === "meeting" && <MeetingActions meeting={e} onChanged={onChanged} dayKey={dayKey} />}
                  {e.type === "followup" && <FollowupActions event={e} onChanged={onChanged} />}
                </div>
              );
            })}
          </div>
        )}
        <Button onClick={() => onAdd(date)} variant="outline" className="rounded-2xl w-full mt-1" data-testid="day-panel-add-meeting">
          <Plus className="h-4 w-4 mr-1" /> New meeting on this day
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Calendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("week");
  // Admin-only: "mine" (default) shows the admin's own events; "others" pulls
  // every other user's follow-ups + meetings together so they can see the
  // whole team's schedule at a glance.
  const [scope, setScope] = useState("mine");
  // Admin-only: pivot the calendar to ONE salesperson (corrections doc).
  const [ownerFilter, setOwnerFilter] = useState("");
  const [teamUsers, setTeamUsers] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => setTeamUsers(r.data || [])).catch(() => setTeamUsers([]));
  }, [isAdmin]);
  const [cursor, setCursor] = useState(() => { const x = new Date(); x.setHours(0,0,0,0); return x; });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMeeting, setOpenMeeting] = useState(false);
  const [defaultStart, setDefaultStart] = useState(null);
  const [defaultType, setDefaultType] = useState("meeting");
  const [dayPanel, setDayPanel] = useState(null);

  const today = useMemo(() => { const x = new Date(); x.setHours(0,0,0,0); return x; }, []);

  const range = useMemo(() => {
    // Pad the fetch window ±1 day: meetings are stored as UTC timestamps, so
    // an event on the window's first local day (IST) can sit on the previous
    // UTC day and get excluded by the backend's string-range query. Client-side
    // day-bucketing (eventDayKeys) filters the extras out anyway.
    if (view === "month") {
      const s = startOfWeek(startOfMonth(cursor));
      const e = addDays(s, 41);
      return { from: isoDate(addDays(s, -1)), to: isoDate(addDays(e, 1)) };
    }
    const s = startOfWeek(cursor);
    return { from: isoDate(addDays(s, -1)), to: isoDate(addDays(s, 7)) };
  }, [cursor, view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/events", {
        params: {
          from_date: range.from,
          to_date: range.to + "T23:59:59",
          // Admin pivots: a single salesperson beats the Mine/Team toggle.
          owner_id: isAdmin && ownerFilter ? ownerFilter : undefined,
          scope: isAdmin && !ownerFilter && scope === "others" ? "others" : undefined,
        },
      });
      setEvents(data);
    } catch (err) { toast.error(fmtError(err)); } finally { setLoading(false); }
  }, [range.from, range.to, isAdmin, scope, ownerFilter]);

  useEffect(() => { load(); }, [load]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (!e.start_at) return;
      // Expand multi-day events across every LOCAL day they span (not just
      // the start day) — same expansion the Week view uses, so both views
      // always stay in sync.
      eventDayKeys(e).forEach((day) => {
        (map[day] = map[day] || []).push(e);
      });
    });
    return map;
  }, [events]);

  const label = view === "month"
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : (() => { const s = startOfWeek(cursor); const e = addDays(s, 6); return `${MONTHS[s.getMonth()]} ${s.getDate()} – ${MONTHS[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`; })();

  const move = (dir) => setCursor(view === "month" ? addMonths(cursor, dir) : addDays(cursor, dir * 7));

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1280px] mx-auto">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Your day</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Calendar.</h1>
          <p className="text-sm text-muted-foreground mt-2">Follow-ups + meetings + activities — all in one place.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Admin-only: switch between "Mine" and "Everyone else" view.
              Pulls every other rep's follow-ups + meetings together so admin
              can scan team load at a glance. */}
          {isAdmin && (
            <div className="inline-flex rounded-2xl border border-border/60 bg-muted/40 p-1 shadow-soft" data-testid="cal-scope-toggle">
              {[
                { k: "mine", label: "Mine" },
                { k: "others", label: "Team" },
              ].map((s) => (
                <button
                  key={s.k}
                  type="button"
                  onClick={() => { setScope(s.k); setOwnerFilter(""); }}
                  data-testid={`cal-scope-${s.k}`}
                  className={`h-8 px-4 rounded-xl text-xs font-semibold tap transition ${scope === s.k && !ownerFilter ? "bg-primary text-primary-foreground shadow-soft" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
          {isAdmin && teamUsers.length > 0 && (
            <Select value={ownerFilter || "__all"} onValueChange={(v) => setOwnerFilter(v === "__all" ? "" : v)}>
              <SelectTrigger className="rounded-2xl h-10 w-[170px]" data-testid="cal-salesperson-filter">
                <SelectValue placeholder="Salesperson" />
              </SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__all">All (use Mine/Team)</SelectItem>
                {teamUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => { setDefaultStart(null); setDefaultType("demo"); setOpenMeeting(true); }} variant="outline" className="rounded-2xl tap border-primary-200/80 text-primary-700 bg-primary-50/60 hover:bg-primary-50" data-testid="new-demo-button">
            <Plus className="h-4 w-4 mr-1" /> New demo
          </Button>
          <Button onClick={() => { setDefaultStart(null); setDefaultType("meeting"); setOpenMeeting(true); }} className="rounded-2xl tap" data-testid="new-meeting-button">
            <Plus className="h-4 w-4 mr-1" /> New meeting
          </Button>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" />Meeting</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" />Follow-up due</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" />Pinged</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-sky-500" />Outlook</span>
        <span className="text-muted-foreground/70 hidden sm:inline">·</span>
        <a href="/account?tab=calendar" className="font-semibold text-primary hover:underline" data-testid="manage-feeds-link">Connect Outlook calendar →</a>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-2xl tap" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <h2 className="font-display text-xl font-extrabold tracking-tight px-3">{label}</h2>
          <Button variant="outline" size="icon" className="rounded-2xl tap" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setCursor(today)}>Today</Button>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="rounded-2xl">
            <TabsTrigger value="month" className="rounded-xl" data-testid="cal-view-month">Month</TabsTrigger>
            <TabsTrigger value="week" className="rounded-xl" data-testid="cal-view-week">Week</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <Skeleton className="h-[560px] rounded-3xl" />
      ) : view === "month" ? (
        <MonthGrid cursor={cursor} eventsByDay={eventsByDay} today={today} onDayClick={(d) => setDayPanel(d)} />
      ) : (
        <WeekList cursor={cursor} events={events} today={today} onChanged={load} />
      )}

      <DayPanel
        date={dayPanel}
        events={dayPanel ? (eventsByDay[isoDate(dayPanel)] || []) : []}
        onClose={() => setDayPanel(null)}
        onChanged={load}
        onAdd={(d) => { setDayPanel(null); setDefaultStart(d.toISOString()); setDefaultType("meeting"); setOpenMeeting(true); }}
      />

      <NewMeetingDialog open={openMeeting} onOpenChange={setOpenMeeting} onCreated={load} defaultStart={defaultStart} defaultType={defaultType} />
    </div>
  );
}
